import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ChatBotSettingsFormValues } from "@jugl-web/domain-resources/chat-bot";
import { TextInput } from "@jugl-web/ui-components";
import { cx, useTranslations } from "@jugl-web/utils";
import { FC } from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { ReactComponent as ReorderIcon } from "./assets/reorder.svg";

const SortableSuggestionInput: FC<{
  item: ChatBotSettingsFormValues["suggestions"][number];
  index: number;
  disabled: boolean;
}> = ({ item, index, disabled }) => {
  const { t } = useTranslations();
  const { control } = useFormContext<ChatBotSettingsFormValues>();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-2">
      <Controller
        control={control}
        name={`suggestions.${index}.value`}
        render={({ field: { onChange, ...props } }) => (
          <TextInput
            labelPosition="border-overlap"
            label={t(
              {
                id: "client-support-page.suggestion-number",
                defaultMessage: "Suggestion {number}",
              },
              { number: index + 1 }
            )}
            placeholder={t({ id: "common.enter", defaultMessage: "Enter" })}
            classNames={{
              wrapperClassName: "w-full",
            }}
            onChange={(e) => onChange(e.target.value.trimStart())}
            {...props}
          />
        )}
      />
      <div
        className={cx(
          disabled
            ? "cursor-not-allowed opacity-40"
            : "cursor-grab touch-none active:cursor-grabbing"
        )}
        {...(!disabled ? attributes : {})}
        {...(!disabled ? listeners : {})}
      >
        <ReorderIcon />
      </div>
    </div>
  );
};

export const AppearanceSuggestionsTab: FC = () => {
  const { t } = useTranslations();
  const { watch, control } = useFormContext<ChatBotSettingsFormValues>();
  const { fields, move } = useFieldArray<ChatBotSettingsFormValues>({
    control,
    name: "suggestions",
  });

  const suggestions = watch("suggestions") || [];

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = fields.findIndex((item) => item.id === active.id);
      const newIndex = fields.findIndex((item) => item.id === over.id);

      move(oldIndex, newIndex);
    }
  };

  const filledCount = suggestions.filter((s) => s.value.trim() !== "").length;
  const isDragDisabled = filledCount < 2;

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-1">
        <span className="font-secondary text-lg font-medium text-black">
          {t({
            id: "client-support-page.suggestions",
            defaultMessage: "Suggestions",
          })}
        </span>
        <span className="text-dark-700 font-secondary text-sm leading-[140%] tracking-[0.14px]">
          {t({
            id: "client-support-page.suggestions-description",
            defaultMessage:
              "Add quick action buttons for common customer requests. They appear at the start of the chat to help visitors engage faster⚡",
          })}
        </span>
      </div>

      <div className="flex flex-col gap-6">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={fields}
            strategy={verticalListSortingStrategy}
          >
            {fields.map((field, index) => (
              <SortableSuggestionInput
                key={field.id}
                item={field}
                index={index}
                disabled={isDragDisabled}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
};
